import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL_ATTACHMENT_FILTERING_RULES_GRID } from '../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  nameCellCustomRenderer,
  PREDEFINED_FILTERS_KEYS,
  statusCellCustomRenderer,
} from './grid-config';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { GzExchangeContentControlAttachmentFilteringRulesGridDataProvider } from './GzExchangeContentControlAttachmentFilteringRulesGridDataProvider';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';
import { GzExchangeContentControlAttachmentFilteringRulesFormWebComponent } from './rulesItemForm';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { validateDuplicates } from './utils/validators';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { FontSizes, FontWeights } from '../../../../../../../../types/common/types';
import { ExchangeContentControlAttachmentFilteringRule } from '@nirvana/shared/dtos/types/policies/common';
import { ExchangeContentControlAttachmentFilteringRuleFormValue } from '../../../../../utils/uiSettings/exchange/exchange-protection.types';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL_ATTACHMENT_FILTERING_RULES_GRID)
export class GzExchangeContentControlAttachmentFilteringRulesGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ExchangeContentControlAttachmentFilteringRule,
  ExchangeContentControlAttachmentFilteringRuleFormValue,
  GzExchangeContentControlAttachmentFilteringRulesGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected columnRenderers = {
    [ColumnKeys.NAME]: nameCellCustomRenderer.bind(null, this.onRuleNameClick.bind(this)),
    [ColumnKeys.STATUS]: statusCellCustomRenderer,
  };
  protected showHeaderCustomActionsInsideGrid = true;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;

  protected hasPriorityColumn = true;
  protected hasMoveToTopAction = true;
  protected hasCloneRowAction = true;

  protected translations = {
    editRow: translateFn('Edit priority'),
    deleteRow: translateFn('Delete'),
    moveToTop: translateFn('Move to top'),
    cloneRow: translateFn('Clone'),
  };

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;
  @query('#clone-button')
  protected cloneButton: GzButtonWebComponent;

  protected getDataProvider(): GzExchangeContentControlAttachmentFilteringRulesGridDataProvider {
    return new GzExchangeContentControlAttachmentFilteringRulesGridDataProvider(this.initialGridItems);
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.LIGHT}">${translateFn('No rules')}</gz-text>

      <gz-button
        id="empty-state-add-button"
        type="${ButtonTypes.QUIET}"
        buttonHeight="${ButtonHeights.SMALL}"
        buttonFontSize="${ButtonFontSizes.SMALL}"
        style="margin-top: 5px;"
        @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Adjust your filters or start adding rules.')}
      </gz-button>
    `;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-button id="add-button" type="${ButtonTypes.OUTLINE}" @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Add')}
      </gz-button>

      <gz-button id="clone-button" type="${ButtonTypes.OUTLINE}" @click="${this.onCloneButtonClick.bind(this)}"
        >${translateFn('Clone')}
      </gz-button>

      <gz-button
        id="delete-button"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.NEGATIVE}"
        ?disabled
        @click="${this.onDeleteSelectedItemsWithDialog.bind(
          this,
          translateFn('Delete rules?'),
          translateFn('You are about to delete the selected rules. This action cannot be undone.')
        )}"
        >${translateFn('Delete')}
      </gz-button>
    `;
  }

  protected validateGridItemBeforeAdd(
    gridItemFormValue: ExchangeContentControlAttachmentFilteringRuleFormValue
  ): ValidationResult {
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzExchangeContentControlAttachmentFilteringRulesGridDataProvider).items
    );
  }

  protected validateGridItemBeforeEdit(
    oldItem: ExchangeContentControlAttachmentFilteringRuleFormValue,
    newItem: ExchangeContentControlAttachmentFilteringRuleFormValue
  ): ValidationResult {
    // skip if item name is not changed
    if (newItem.name === oldItem.name) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);

    // buttons should be disabled if default rule is selected
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
    this.cloneButton.disabled = event.detail.selectionStoreData.length !== 1;
  }

  private onAddButtonClick(): void {
    const maxPriority =
      (this.gridComponent.dataProvider as GzExchangeContentControlAttachmentFilteringRulesGridDataProvider).items
        .length + 1;
    const addRuleCallback = (formValue: ExchangeContentControlAttachmentFilteringRuleFormValue): ValidationResult =>
      this.onNewGridItemAdd(formValue, formValue.defaultPositionInGrid);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeContentControlAttachmentFilteringRulesFormWebComponent(maxPriority),
      strings: {
        title: translateFn('Add attachment filtering rule'),
        confirm: translateFn('Add'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private onRuleNameClick(item: ExchangeContentControlAttachmentFilteringRuleFormValue): void {
    const maxPriority = (
      this.gridComponent.dataProvider as GzExchangeContentControlAttachmentFilteringRulesGridDataProvider
    ).items.length;
    const addRuleCallback = (formValue: ExchangeContentControlAttachmentFilteringRuleFormValue): ValidationResult =>
      this.onEditGridItemFromPopup(formValue, item);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeContentControlAttachmentFilteringRulesFormWebComponent(maxPriority, item),
      strings: {
        title: translateFn('Edit attachment filtering rule'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected onCloneRow(event: CustomEvent) {
    this.cloneRule(event.detail.rowData.item);
  }

  private onCloneButtonClick(): void {
    if (!this.hasSelectableRows) {
      return;
    }

    // a single item can be cloned
    const itemPosition = this.selectedItemsGridData[0];
    const item = (this.gridComponent.dataProvider as GzExchangeContentControlAttachmentFilteringRulesGridDataProvider)
      .items[itemPosition - 1];

    this.cloneRule(item);
  }

  private cloneRule(item: ExchangeContentControlAttachmentFilteringRule): void {
    // set max priority
    const maxPriority =
      (this.gridComponent.dataProvider as GzExchangeContentControlAttachmentFilteringRulesGridDataProvider).items
        .length + 1;
    const addRuleCallback = (formValue: ExchangeContentControlAttachmentFilteringRuleFormValue): ValidationResult =>
      this.onNewGridItemAdd(formValue, formValue.defaultPositionInGrid);

    // create new item
    const newItem = {
      ...item,
      defaultPositionInGrid: 1, // by default, a new rule will be added at the top of the list
      name: this.getSuggestedNameForClonedItem(item.name, 'name'),
      active: true,
    };

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeContentControlAttachmentFilteringRulesFormWebComponent(maxPriority, newItem),
      strings: {
        title: translateFn('Add attachment filtering rule'),
        confirm: translateFn('Add'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected onGridItemsChange(): void {
    super.onGridItemsChange();
    this.dispatchEvent(new CustomEvent('change', { bubbles: true, composed: true }));
  }
}
